package com.wintermar.inspection.core.ui.component.image.util

import com.wintermar.inspection.core.data.Uuid
import io.github.vinceglb.filekit.PlatformFile
import io.github.vinceglb.filekit.extension
import io.github.vinceglb.filekit.name
import io.github.vinceglb.filekit.size
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext

expect suspend fun compress(file: PlatformFile, targetSizeKb: Int): ByteArray
expect fun streamChunkFiles(
    file: FileModel,
    targetSizeKb: Int = 50
): Flow<FileChunkModel>

/**
 * Delete temporary file. This is a best-effort operation.
 * The function does not provide success/failure feedback because:
 * - Platform implementations may use different cleanup strategies
 * - Cleanup is non-critical and failures are silently acceptable
 * - File may have already been cleaned up by the OS
 *
 * @param file The file to delete
 */
expect suspend fun deleteTemporaryFile(file: PlatformFile)

object FileUtil {
    suspend fun compressToTargetSize(
        file: PlatformFile,
        generateChunk: Boolean = true,
        targetSizeKb: Int = 500,
    ): FileModel = withContext(Dispatchers.Default) {
        val fileId = Uuid.generate()
        val name = "${Uuid.generate()}.${file.extension}"
        val compressedBytes =
            if (FileGroupTypes.IMAGES.getExtensionsWithoutDot().contains(file.extension)) compress(
                file,
                targetSizeKb
            )
            else null
        var fileResult = FileModel(
            id = fileId,
            name = name,
            path = if (compressedBytes != null) "" else file.toString(),
            originalName = file.name,
            type = file.extension,
            size = compressedBytes?.size?.toLong() ?: file.size(),
            blob = compressedBytes,
            platformFile = file
        )
        if (generateChunk && compressedBytes != null) {
            fileResult = fileResult.copy(
                chunkFiles = mutableListOf<FileChunkModel>().apply {
                    streamChunkFiles(fileResult).collect { add(it) }
                },
                hasGenerateChunk = true
            )
        }
        fileResult
    }

    fun getDisplaySizeInKb(value: Int): String {
        val sizeInKb = value / 1024
        val formatted = sizeInKb.toString()
            .reversed()
            .chunked(3)
            .joinToString(".")
            .reversed()
        return "${formatted}kb"
    }

    fun createChunk(
        file: FileModel,
        index: Int,
        bytes: ByteArray,
        totalSize: Int,
        totalChunks: Int
    ) =
        FileChunkModel(
            id = Uuid.generate(),
            fileId = file.id,
            fileName = file.name,
            originalFileName = file.originalName,
            chunkNumber = index,
            chunkSize = bytes.size,
            fileSize = totalSize,
            totalFiles = totalChunks,
            blob = bytes
        )

    fun getExtensionFromFileName(filename: String): String {
        return filename.substringAfterLast('.')
    }
}
